import type { PostHog } from 'posthog-js';
import type { AnalyticsConfig, SharedEventProperties } from './types';
import { detectPlatform } from './platform';

// Module-level config — set once by initAnalytics() during AnalyticsProvider
// render. Intentional: we want a singleton-ish state across the app's
// lifetime so trackEvent() / resetAnalytics() can run without context.
// Caveat: in unit tests or HMR reloads the module is reused; tests that
// exercise reset paths should call initAnalytics() in their setup.
let _config: AnalyticsConfig | null = null;
let _sharedProps: SharedEventProperties | null = null;

/**
 * Initialize the analytics module with app-specific config.
 * Must be called once at app startup (AnalyticsProvider handles this).
 */
export function initAnalytics(config: AnalyticsConfig): void {
  _config = config;
  _sharedProps = {
    app: config.app,
    partner: config.partner ?? null,
    platform: detectPlatform(),
  };
}

export function getSharedEventProperties(): Record<string, unknown> {
  return { ...(_sharedProps || {}), source: 'client', schema_version: 1 };
}

function getLogEventUrl(): string {
  return _config?.logEventUrl || 'https://log-event-613409150384.us-east1.run.app/';
}

function isDebug(): boolean {
  return _config?.debug ?? false;
}

export function isPostHogConfigured(): boolean {
  return Boolean(_config?.posthogApiKey);
}

interface NonIdCommonEventProperties {
  deviceType?: string | null;
  os?: string | null;
  pageUrl?: string | null;
}

const getNonIdCommonEventProperties = (posthog: PostHog | undefined): NonIdCommonEventProperties => {
  if (!posthog) {
    return { pageUrl: window.location.href };
  }

  return {
    deviceType: posthog.get_property('$device_type') ?? null,
    os: posthog.get_property('$os') ?? null,
    pageUrl: posthog.get_property('$current_url') || window.location.href,
  };
};

/**
 * Track a custom analytics event.
 * Sends to GCP Cloud Run log-event → Supabase analytics_events → PostHog.
 * Automatically injects shared properties (app, partner, platform).
 * Returns whether the ingestion endpoint confirmed delivery. Callers that
 * maintain durable deduplication should only commit it after this is true.
 */
export const trackEvent = async (
  eventName: string,
  properties: Record<string, unknown> = {},
  posthogInstance: PostHog | undefined,
  currentUserId?: string | null,
  dbAnonymousId?: string | null
): Promise<boolean> => {
  if (!posthogInstance) {
    if (isDebug()) console.warn(`[analytics] PostHog not available. Event "${eventName}" not tracked.`);
    return false;
  }

  const commonNonIdProps = getNonIdCommonEventProperties(posthogInstance);

  // Merge shared properties (app, partner, platform) into event properties
  const mergedProperties = {
    ...properties,
    ...getSharedEventProperties(),
  };

  const anonymousId =
    dbAnonymousId ||
    (!currentUserId ? posthogInstance.get_distinct_id() : null);

  if (!currentUserId && !anonymousId) {
    if (isDebug()) {
      console.warn(`[analytics] Anonymous ID unavailable. Event "${eventName}" not tracked.`);
    }
    return false;
  }

  const payload = {
    eventName,
    properties: mergedProperties,
    userId: currentUserId || null,
    anonymousId: anonymousId || null,
    ...commonNonIdProps,
  };

  if (isDebug()) {
    console.log(`[analytics] ${eventName}`, mergedProperties);
  }

  try {
    const response = await fetch(getLogEventUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      // Survive page navigation/unload — unmount-time events (text_closed,
      // corridor_dropped, ws_disconnected) are silently dropped without this.
      // Note: browsers cap keepalive request bodies at 64KB; analytics
      // payloads are far below that (≤20 primitive props).
      keepalive: true,
    });

    if (!response.ok) {
      if (isDebug()) {
        const errorData = await response.json().catch(() => ({}));
        console.error(`[analytics] Failed to track "${eventName}":`, response.status, errorData);
      }
      return false;
    }

    return true;
  } catch (error) {
    if (isDebug()) {
      console.error(`[analytics] Error tracking "${eventName}":`, error);
    }
    return false;
  }
};

/**
 * Identify a user in PostHog and set their properties.
 * Call on login or signup completion.
 */
export const identifyUser = (
  userId: string,
  properties: Record<string, unknown> = {},
  posthogInstance: PostHog | undefined
): void => {
  if (!posthogInstance) return;
  posthogInstance.identify(userId, properties);
};

/**
 * Alias a previously anonymous user to an identified user ID.
 * Call on signup completion, before identifyUser.
 */
export const aliasUser = (
  userId: string,
  previousDistinctId: string,
  posthogInstance: PostHog | undefined
): void => {
  if (!posthogInstance) return;
  if (userId !== previousDistinctId) {
    posthogInstance.alias(userId, previousDistinctId);
  }
};

/**
 * Reset PostHog identification. Call on logout.
 *
 * `posthog.reset()` clears registered super-properties, so we re-register
 * the shared {app, partner, platform} props immediately after to keep them
 * on every event for the rest of the session.
 */
export const resetAnalytics = (posthogInstance: PostHog | undefined): void => {
  if (!posthogInstance) return;
  posthogInstance.reset();
  if (_sharedProps) {
    posthogInstance.register(getSharedEventProperties());
  } else {
    // Detectable in staging: super-properties stay cleared for the rest of
    // the session if initAnalytics() never ran before reset (test/SSR paths).
    console.warn('[analytics] resetAnalytics called before initAnalytics — super-properties cleared without re-register');
  }
};

/**
 * Track signup_start event with UTM parameters and referrer.
 */
export const trackSignupStart = async (
  context: string,
  posthogInstance: PostHog | undefined,
  additionalProperties: Record<string, unknown> = {}
): Promise<void> => {
  if (!posthogInstance) return;

  const anonymousIdForDB = posthogInstance.get_distinct_id();
  const signupProperties: Record<string, unknown> = {
    context,
    referrer: document.referrer || undefined,
    utm_source: new URLSearchParams(window.location.search).get('utm_source') || undefined,
    utm_medium: new URLSearchParams(window.location.search).get('utm_medium') || undefined,
    utm_campaign: new URLSearchParams(window.location.search).get('utm_campaign') || undefined,
    utm_content: new URLSearchParams(window.location.search).get('utm_content') || undefined,
    utm_term: new URLSearchParams(window.location.search).get('utm_term') || undefined,
    ...additionalProperties,
  };

  const cleanProps = Object.fromEntries(
    Object.entries(signupProperties).filter(([_, v]) => v != null)
  );

  await trackEvent('signup_start', cleanProps, posthogInstance, null, anonymousIdForDB);
};

/**
 * Track signup_complete event.
 *
 * The `email` param is accepted for caller compatibility but intentionally
 * NOT sent in event properties — house standard: no PII beyond user id in
 * analytics props (the user_id column already identifies the row).
 * Returns whether the ingestion endpoint confirmed delivery.
 */
export const sendSignupCompleteEvent = async (
  userId: string,
  _email: string | undefined,
  method: string,
  previousAnonymousIdForDB: string,
  posthogInstance: PostHog | undefined
): Promise<boolean> => {
  const eventProperties = {
    method,
    first_time: true,
  };

  return trackEvent('signup_complete', eventProperties, posthogInstance, userId, previousAnonymousIdForDB);
};

/**
 * Track search_performed event.
 */
export const trackSearchPerformed = async (
  query: string,
  context: string,
  resultsCount: number,
  posthogInstance: PostHog | undefined,
  currentUserId?: string | null
): Promise<void> => {
  if (!posthogInstance) return;

  const properties = {
    query,
    context,
    results_count: resultsCount,
  };

  const dbAnonymousId = currentUserId ? null : posthogInstance.get_distinct_id();
  await trackEvent('search_performed', properties, posthogInstance, currentUserId, dbAnonymousId);
};
